'use client';
import React, { useState } from 'react';
import { Search, ArrowRight, ShoppingBag, Car, Briefcase, Home, Pill, Scissors, Utensils, Truck, Package, Globe, Zap, Star, Users, TrendingUp, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';

interface MarketModule {
  id: string;
  name: string;
  description: string;
  href: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  listings: string;
  users: string;
  category: string;
  badge?: string;
}

const MODULES: MarketModule[] = [
  { id: 'classifieds', name: 'Classifieds', description: 'Buy & sell anything — electronics, furniture, appliances', href: '/classifieds', icon: ShoppingBag, color: '#FFB300', bgColor: 'rgba(255,179,0,0.12)', listings: '28K+', users: '45K+', category: 'General', badge: 'Popular' },
  { id: 'vehicles', name: 'Vehicles', description: 'New & used cars, motorcycles, trucks, and more', href: '/vehicles', icon: Car, color: '#78909C', bgColor: 'rgba(120,144,156,0.12)', listings: '8.9K+', users: '15K+', category: 'Automotive' },
  { id: 'jobs', name: 'Jobs', description: 'Job listings, internships, and career opportunities', href: '/jobs', icon: Briefcase, color: '#00BCD4', bgColor: 'rgba(0,188,212,0.12)', listings: '12K+', users: '34K+', category: 'Employment', badge: 'Hot' },
  { id: 'property', name: 'Property', description: 'Houses, apartments, land, and commercial spaces', href: '/property', icon: Home, color: '#E040FB', bgColor: 'rgba(224,64,251,0.12)', listings: '6.7K+', users: '18K+', category: 'Real Estate' },
  { id: 'pharmacy', name: 'Pharmacy', description: 'Medicines, health products, and wellness items', href: '/pharmacy', icon: Pill, color: '#40C4FF', bgColor: 'rgba(64,196,255,0.12)', listings: '4.5K+', users: '8.9K+', category: 'Health' },
  { id: 'beauty', name: 'Beauty & Fashion', description: 'Clothing, accessories, cosmetics, and salon services', href: '/beauty', icon: Scissors, color: '#F06292', bgColor: 'rgba(240,98,146,0.12)', listings: '15K+', users: '23K+', category: 'Fashion' },
  { id: 'food', name: 'Food & Restaurants', description: 'Order food, discover restaurants, and catering', href: '/food', icon: Utensils, color: '#FF5722', bgColor: 'rgba(255,87,34,0.12)', listings: '2.3K+', users: '18K+', category: 'Food & Beverage', badge: 'New' },
  { id: 'transport', name: 'Transport', description: 'Matatus, taxis, buses, and private hire services', href: '/transport', icon: Truck, color: '#42A5F5', bgColor: 'rgba(66,165,245,0.12)', listings: '1.2K+', users: '12K+', category: 'Logistics' },
  { id: 'auto-parts', name: 'Auto Parts', description: 'Spare parts, tyres, and vehicle accessories', href: '/auto-parts', icon: Package, color: '#546E7A', bgColor: 'rgba(84,110,122,0.12)', listings: '12K+', users: '8.9K+', category: 'Automotive' },
  { id: 'imported-products', name: 'Imported Products', description: 'China, UK, and Sweden sourced products', href: '/imported-products', icon: Globe, color: '#EF5350', bgColor: 'rgba(239,83,80,0.12)', listings: '14K+', users: '11K+', category: 'Imports' },
  { id: 'machinery', name: 'Machinery', description: 'Industrial equipment, tools, and heavy machinery', href: '/machinery', icon: Zap, color: '#FFA726', bgColor: 'rgba(255,167,38,0.12)', listings: '3.4K+', users: '5.6K+', category: 'Industrial' },
  { id: 'business', name: 'Business Directory', description: 'Find verified local businesses and services', href: '/business', icon: Star, color: '#29B6F6', bgColor: 'rgba(41,182,246,0.12)', listings: '4.5K+', users: '8.9K+', category: 'Services' },
];

const CATEGORIES = ['All', 'General', 'Automotive', 'Employment', 'Real Estate', 'Health', 'Fashion', 'Food & Beverage', 'Logistics', 'Imports', 'Industrial', 'Services'];

const PLATFORM_STATS = [
  { label: 'Active Listings', value: '120K+', icon: ShoppingBag, color: '#00C853' },
  { label: 'Registered Users', value: '250K+', icon: Users, color: '#00BCD4' },
  { label: 'Daily Transactions', value: '8,500+', icon: TrendingUp, color: '#FFB300' },
  { label: 'Cities Covered', value: '47', icon: Globe, color: '#E040FB' },
];

export default function MarketplacePage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');

  const filtered = MODULES.filter(m => {
    const matchSearch = !search || m.name.toLowerCase().includes(search.toLowerCase()) || m.description.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All' || m.category === category;
    return matchSearch && matchCat;
  });

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        {/* Hero */}
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #0a1020 0%, #0d1530 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
            style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #00C853 0%, transparent 45%), radial-gradient(circle at 80% 30%, #FFB300 0%, transparent 45%), radial-gradient(circle at 50% 80%, #7C4DFF 0%, transparent 40%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600 mb-4"
              style={{ background: 'rgba(0,200,83,0.15)', color: '#00C853', border: '1px solid rgba(0,200,83,0.3)' }}>
              <ShoppingBag size={12} /> Kenya&apos;s Largest Marketplace
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              One Platform, <span style={{ color: '#00C853' }}>Every Market</span>
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Buy, sell, hire, and discover across 12 specialized marketplaces — all in one place.
            </p>
            <div className="relative max-w-md mx-auto">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search marketplaces…"
                className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm outline-none"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
            </div>
          </div>
        </div>

        {/* Platform Stats */}
        <div className="px-4 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-3">
            {PLATFORM_STATS.map(s => (
              <div key={s.label} className="flex items-center gap-3 p-3 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: s.color + '20' }}>
                  <s.icon size={14} style={{ color: s.color }} />
                </div>
                <div>
                  <p className="text-sm font-800 text-white">{s.value}</p>
                  <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Category Filter */}
        <div className="px-4 py-3 sticky top-0 z-20"
          style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setCategory(c)}
                className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
                style={{ background: category === c ? 'rgba(0,200,83,0.15)' : 'rgba(255,255,255,0.04)', color: category === c ? '#00C853' : 'rgba(255,255,255,0.55)', border: `1px solid ${category === c ? 'rgba(0,200,83,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Module Grid */}
        <div className="max-w-4xl mx-auto px-4 pt-5 pb-10">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span style={{ color: '#00C853', fontWeight: 700 }}>{filtered.length}</span> marketplaces
            </span>
            <Link href="/login"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
              style={{ background: '#00C853', color: '#000' }}>
              Start Selling <ArrowRight size={12} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filtered.map(mod => (
              <Link key={mod.id} href={mod.href}
                className="group p-4 rounded-2xl transition-all hover:scale-[1.01]"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: mod.bgColor, border: `1px solid ${mod.color}30` }}>
                    <mod.icon size={22} style={{ color: mod.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="text-sm font-700 text-white">{mod.name}</h3>
                      {mod.badge && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-700"
                          style={{ background: mod.badge === 'Hot' ? 'rgba(255,59,92,0.2)' : mod.badge === 'New' ? 'rgba(0,200,83,0.2)' : 'rgba(255,179,0,0.2)', color: mod.badge === 'Hot' ? '#FF3B5C' : mod.badge === 'New' ? '#00C853' : '#FFB300' }}>
                          {mod.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-xs line-clamp-1" style={{ color: 'rgba(255,255,255,0.45)' }}>{mod.description}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
                        <span style={{ color: mod.color, fontWeight: 700 }}>{mod.listings}</span> listings
                      </span>
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
                        <span style={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>{mod.users}</span> users
                      </span>
                    </div>
                  </div>
                  <ChevronRight size={14} className="flex-shrink-0 mt-1 transition-transform group-hover:translate-x-0.5" style={{ color: 'rgba(255,255,255,0.25)' }} />
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>No marketplaces match your search</p>
            </div>
          )}

          {/* CTA Banner */}
          <div className="mt-8 p-6 rounded-3xl"
            style={{ background: 'linear-gradient(135deg, rgba(0,200,83,0.1), rgba(0,188,212,0.08), rgba(124,77,255,0.1))', border: '1px solid rgba(0,200,83,0.2)' }}>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-800 text-white mb-1">Ready to Start Selling?</h3>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>Join 250,000+ sellers across Kenya. Free to list, easy to manage.</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Link href="/login"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-2xl font-700 text-sm"
                  style={{ background: '#00C853', color: '#000' }}>
                  <ShoppingBag size={15} /> Post a Listing
                </Link>
                <Link href="/sign-up-login-screen"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-2xl font-700 text-sm"
                  style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.12)' }}>
                  Sign Up Free
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
